/**
 * HALO Card Component
 *
 * Preserved per HP-7685 AC-2 + HP-7690 audit: HotPads' compound API
 * (`Card.Header`/`Card.Body`/`Card.Title`/`Card.Icon`), `headerLayout`
 * variants (default/space-between/space-around/flex-start/flex-end), and
 * collapsible feature have no Constellation v10 equivalent. Constellation
 * `PropertyCard` is photo-led for SRP listing display, and the regular
 * Constellation `Card` is a styled `<div>` with no compound surface.
 *
 * Supports flexible content composition with progressive enhancement
 * for accessibility.
 *
 * Features:
 * - Three visual variants (default, accent, minimal)
 * - Three size variants (sm, md, lg) for padding
 * - Optional collapsible functionality with WCAG compliance
 * - Progressive enhancement - content visible by default
 * - Flexible header composition (icon, title, actions)
 * - Completely flexible body content
 */

import React, { useId, useState } from 'react';
import { css, cx } from '@/styled-system/css';
import {
  cardBase,
  cardBody,
  cardCollapseToggle,
  cardFooter,
  cardHeaderVariants,
  cardIcon,
  cardTitle,
  cardVariants,
} from './card.styles';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { CardProps } from './types';

export const Card = React.forwardRef<HTMLElement, CardProps>(
  (
    {
      variant = 'default',
      size = 'md',
      title,
      titleSize = 'lg',
      icon,
      header,
      headerLayout = 'default',
      headerActions,
      children,
      footer,
      collapsible = false,
      defaultCollapsed = false,
      collapsed: controlledCollapsed,
      onCollapseChange,
      className,
      id,
      ...restProps
    },
    ref,
  ) => {
    // Generate unique IDs only for accessibility relationships that need them.
    // Non-collapsible cards don't need generated ids in the DOM, and emitted
    // useId values can be noisy across streamed server/client boundaries.
    const generatedId = useId();
    const cardId = id ?? (collapsible ? generatedId : undefined);
    const bodyId = collapsible ? `${cardId ?? generatedId}-body` : undefined;
    const toggleId = collapsible ? `${cardId ?? generatedId}-toggle` : undefined;

    // State management for collapsible functionality
    // Use controlled state if provided, otherwise fall back to internal state
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;

    // Handle collapse toggle with proper callback
    const handleToggle = () => {
      const newCollapsed = !isCollapsed;

      // Update internal state if uncontrolled
      if (controlledCollapsed === undefined) {
        setInternalCollapsed(newCollapsed);
      }

      // Always call the callback if provided
      onCollapseChange?.(newCollapsed);
    };

    // Handle keyboard navigation for collapse toggle
    const handleToggleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggle();
      }
    };

    // Combine base styles with variants
    const cardClasses = cx(
      cardBase,
      cardVariants({
        variant,
        size,
        collapsible,
      }),
      className,
    );

    // Render collapse toggle button
    const renderCollapseToggle = () => (
      <button
        id={toggleId}
        type="button"
        className={cardCollapseToggle}
        onClick={handleToggle}
        onKeyDown={handleToggleKeyDown}
        aria-expanded={!isCollapsed}
        aria-controls={bodyId}
        aria-label={isCollapsed ? 'Expand card content' : 'Collapse card content'}
      >
        <LucideIcon icon={isCollapsed ? ChevronUp : ChevronDown} size="sm" />
      </button>
    );

    // Render icon with proper wrapper
    const renderIcon = (iconElement: React.ReactNode) => (
      <span className={cardIcon} aria-hidden="true">
        {iconElement}
      </span>
    );

    // Render title with proper styling
    const renderTitle = (titleText: string) => (
      <h3 className={cardTitle} data-size={titleSize}>
        {titleText}
      </h3>
    );

    // Render automatic header when title or icon provided
    const renderAutoHeader = () => {
      if (!title && !icon) return null;

      const headerClasses = cardHeaderVariants({
        layout: headerActions ? 'space-between' : headerLayout,
        align: 'center',
      });

      return (
        <header className={headerClasses}>
          <div className={css({ display: 'flex', alignItems: 'center', gap: 'tighter' })}>
            {icon && renderIcon(icon)}
            {title && renderTitle(title)}
          </div>
          {headerActions && (
            <div className={css({ display: 'flex', alignItems: 'center', gap: '200' })}>{headerActions}</div>
          )}
          {collapsible && renderCollapseToggle()}
        </header>
      );
    };

    // Render custom header
    const renderCustomHeader = () => {
      if (!header) return null;

      const headerClasses = cardHeaderVariants({ layout: 'default', align: 'center' });

      return (
        <header className={headerClasses}>
          {header}
          {collapsible && renderCollapseToggle()}
        </header>
      );
    };

    // Render body with progressive enhancement
    const renderBody = () => (
      <div
        id={bodyId}
        className={cardBody}
        data-collapsed={isCollapsed}
        aria-labelledby={collapsible ? toggleId : undefined}
      >
        {children}
      </div>
    );

    // Render footer if provided
    const renderFooter = () => {
      if (!footer) return null;

      return <div className={cardFooter}>{footer}</div>;
    };

    // Main card render
    return (
      <article ref={ref as React.Ref<HTMLElement>} id={id || cardId} className={cardClasses} {...restProps}>
        {header ? renderCustomHeader() : renderAutoHeader()}
        {renderBody()}
        {renderFooter()}
      </article>
    );
  },
);

Card.displayName = 'Card';

// Export types for consumers
export type { CardProps } from './types';
